import type { ViewAllNotesEntryView } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchViewAllNotesApi } from "../../../api/client";
import { viewAllNotesKeys } from "./viewAllNotesKeys";
import { toViewAllNotesEntry, type ViewAllNotesEntry } from "./viewAllNotesTypes";

/**
 * Shared queryOptions for a deal's Internal-tab View All Notes (key + queryFn).
 * Backed by the generated `WorkbenchViewAllNotesApi.getViewAllNotes`, which
 * forwards to recap-service and re-evaluates the notes list per `filter` — the
 * table renders them in server order (no client-side sort or filter). Rows are
 * normalized at the boundary via {@link toViewAllNotesEntry} so the render path
 * stays free of optional-field handling. `staleTime` matches the app-wide 30s
 * convention.
 *
 * Reusable by useQuery, prefetchQuery, ensureQueryData, getQueryData, and
 * setQueryData.
 * @param {string} approvalId - The numeric approval / vehicle-loan-application id.
 * @param {string} filter - The caps2-parity filter value (e.g. "All", "CAPS", "DEALER").
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function viewAllNotesQuery(approvalId: string, filter: string) {
  return queryOptions({
    queryKey: viewAllNotesKeys.list({ approvalId, filter }),
    queryFn: async ({ signal }): Promise<ViewAllNotesEntry[]> => {
      const response = await workbenchViewAllNotesApi.getViewAllNotes(approvalId, filter, { signal });
      return (response.data.notes ?? []).map((row: ViewAllNotesEntryView) => toViewAllNotesEntry(row));
    },
    staleTime: 30_000,
    enabled: !!approvalId,
  });
}

/**
 * Reads the Internal-tab View All Notes for a deal, filtered server-side by
 * `filter`. Disabled (never fires) until the approval id is present, so
 * callers can render an empty/loading state instead of issuing a doomed
 * request.
 * @param {string} approvalId - The numeric approval / vehicle-loan-application id.
 * @param {string} filter - The caps2-parity filter value (e.g. "All", "CAPS", "DEALER").
 * @returns {ReturnType<typeof useQuery<ViewAllNotesEntry[]>>} The TanStack Query result.
 */
export function useViewAllNotes(approvalId: string, filter: string) {
  return useQuery(viewAllNotesQuery(approvalId, filter));
}
